/**
 * Tag color utilities for consistent tag styling across the application
 */

// Base tag color mapping (RGB values without alpha)
const BASE_TAG_COLORS = {
  // 🔴 RED - Security/Governance
  'security': '244, 67, 54',
  'defense': '229, 57, 53',
  
  // 🟠 ORANGE - Commerce/Services 
  'consumer': '255, 152, 0',
  'fintech': '255, 167, 38',
  
  // 🟡 YELLOW - Digital Intelligence
  'ai': '255, 235, 59',
  'computing': '255, 213, 79',
  'networks': '251, 192, 45',
  'immersive': '255, 160, 0',
  
  // 🟢 GREEN - Living Systems + Research
  'agtech': '139, 195, 74',
  'climate': '104, 159, 56',
  'biotech': '76, 175, 80',
  'health': '67, 160, 71',
  'research': '46, 125, 50',
  
  // 🔵 BLUE - Human Society + Urban
  'society': '33, 150, 243',
  'economics': '30, 136, 229',
  'education': '25, 118, 210',
  'media': '21, 101, 192',
  'urban': '13, 71, 161',
  
  // 🟣 PURPLE - Physical World + Other
  'mobility': '103, 58, 183',
  'robotics': '94, 53, 177',
  'energy': '156, 39, 176',
  'materials': '142, 36, 170',
  'space': '123, 31, 162',
  'other': '74, 20, 140'
};

// Fallback rainbow colors for unknown tags
const RAINBOW_BASE_COLORS = [
  '255, 59, 48',   // Red
  '255, 149, 0',   // Orange
  '255, 204, 0',   // Yellow
  '50, 215, 75',   // Green
  '0, 122, 255',   // Blue
  '88, 86, 214',   // Purple
  '255, 45, 85',   // Pink
  '255, 214, 10',  // Bright Yellow
  '48, 209, 88',   // Light Green
  '120, 87, 255',  // Light Purple
  '64, 200, 224',  // Light Blue
  '255, 105, 180'  // Hot Pink
];

/**
 * Get color for a single tag with theme-based transparency
 * @param {string} tag - The tag name
 * @param {string} themeAlpha - Alpha transparency value (default: '0.3')
 * @returns {string} RGBA color string
 */
export const getTagColor = (tag, themeAlpha = '0.3') => {
  if (BASE_TAG_COLORS[tag]) {
    return `rgba(${BASE_TAG_COLORS[tag]}, ${themeAlpha})`;
  }
  
  // For unknown tags, generate consistent color based on tag name hash
  let hash = 0;
  for (let i = 0; i < tag.length; i++) {
    hash = tag.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colorIndex = Math.abs(hash) % RAINBOW_BASE_COLORS.length;
  
  return `rgba(${RAINBOW_BASE_COLORS[colorIndex]}, ${themeAlpha})`;
};

/**
 * Blend multiple tag colors into a gradient
 * @param {string|Array} tags - Tags as string or array
 * @param {string} themeAlpha - Alpha transparency value (default: '0.3')
 * @returns {string} CSS gradient or solid color string
 */
export const blendColors = (tags, themeAlpha = '0.3') => {
  if (!tags || tags.length === 0) return 'rgba(255, 255, 255, 0.1)';
  
  let tagList = [];
  try {
    // Handle array format like "['tag1', 'tag2']"
    tagList = JSON.parse(tags.replace(/'/g, '"')).filter(tag => tag && tag !== 'All');
  } catch {
    // Fallback: treat as string and split by pipe or comma
    tagList = tags.split(/[|,]/).map(tag => tag.trim()).filter(tag => tag && tag !== 'All');
  }
  
  if (tagList.length === 0) return 'rgba(255, 255, 255, 0.1)';
  if (tagList.length === 1) return getTagColor(tagList[0], themeAlpha);
  
  // For multiple tags, create a layered gradient effect
  const colors = tagList.map(tag => getTagColor(tag, themeAlpha)).filter(Boolean);
  if (colors.length === 0) return 'rgba(255, 255, 255, 0.1)';
  
  // Create gradient from multiple colors
  if (colors.length === 2) {
    return `linear-gradient(135deg, ${colors[0]} 0%, ${colors[1]} 100%)`;
  } else if (colors.length === 3) {
    return `linear-gradient(135deg, ${colors[0]} 0%, ${colors[1]} 50%, ${colors[2]} 100%)`;
  } else {
    // For 4+ colors, use complex gradient
    const step = 100 / colors.length;
    const gradientStops = colors.map((color, index) => `${color} ${index * step}%`).join(', ');
    return `linear-gradient(135deg, ${gradientStops})`;
  }
};

/**
 * Sort tags by color family order
 * @param {Array<string>} tags - Array of tag names
 * @returns {Array<string>} Sorted array
 */
export const sortTagsByColorOrder = (tags) => {
  const colorOrderedTags = [
    // 🔴 RED - Security/Governance
    'security', 'defense',
    // 🟠 ORANGE - Commerce/Services 
    'consumer', 'fintech',
    // 🟡 YELLOW - Digital Intelligence
    'ai', 'computing', 'networks', 'immersive',
    // 🟢 GREEN - Living Systems + Research
    'agtech', 'climate', 'biotech', 'health', 'research',
    // 🔵 BLUE - Human Society + Urban
    'society', 'economics', 'education', 'media', 'urban',
    // 🟣 PURPLE - Physical World + Other
    'mobility', 'robotics', 'energy', 'materials', 'space', 'other'
  ];
  
  return tags.sort((a, b) => {
    // "All" always comes first
    if (a === 'All') return -1;
    if (b === 'All') return 1;
    
    const indexA = colorOrderedTags.indexOf(a);
    const indexB = colorOrderedTags.indexOf(b);
    
    // If both tags are in predefined order, sort by that order
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    
    // If only one tag is in predefined order, it comes first
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    
    // If neither tag is in predefined order, sort alphabetically
    return a.localeCompare(b);
  });
};

